/**
 * English translations.
 *
 * Must have exactly the same keys as `de.ts`. The shared `Translations`
 * interface (from `./index`) enforces this at compile time.
 */

import type { Translations } from './index';

const en: Translations = {
  // ── App-level ──────────────────────────────────────────────────────
  'app.loading':                          'Loading\u2026',
  'app.syncing':                          'Syncing\u2026',
  'app.undo':                             'Undo',
  'app.restored':                         'Restored',
  'app.deleteFailedRestored':             'Delete failed — restored',
  'app.nameRequired':                     'Name is required',
  'app.bugReport':                        'Report a bug',
  'app.bugTitle':                         'Title',
  'app.bugTitlePlaceholder':              'Short description of the problem',
  'app.bugDescription':                   'Description',
  'app.bugDescPlaceholder':               'What happened? What did you expect?',
  'app.bugScreenshot':                    'Attach screenshot (may contain visible invite codes)',
  'app.bugAutoContext':                   'Auto-context',
  'app.bugLastActions':                   'Recent actions',
  'app.bugSubmit':                        'Submit bug report',
  'app.bugSending':                       'Sending\u2026',
  'app.bugEnterTitle':                    'Please enter a title',
  'app.bugThanks':                        'Thanks!',
  'app.bugCreated':                       'Your bug report was created (Issue #{{number}}).',
  'app.bugViewOnGitHub':                  'View on GitHub',
  'app.bugServerNotConfigured':           'Bug reporting is not set up on the server yet — opening GitHub manually.',
  'app.bugError':                         'Error creating bug report',
  'app.householdNotFound':                'Household not found or access denied',
  'app.connectionError':                  'Connection error — please try again',
  'app.householdCreated':                 'Household created',
  'app.householdCreateError':             'Error creating household',
  'app.householdJoined':                  'Joined household',
  'app.invalidCode':                      'Invalid code',
  'app.nameSaved':                        'Name saved',
  'app.noActions':                        'No actions recorded',

  // ── Tab bar ────────────────────────────────────────────────────────
  'nav.home':                             'Home',
  'nav.rooms':                            'Rooms',
  'nav.people':                           'People',
  'nav.household':                        'Household',
  'nav.add':                              'Add',

  // ── Home view ──────────────────────────────────────────────────────
  'home.greet.night':                     'Still up?',
  'home.greet.morning':                   'Good morning',
  'home.greet.afternoon':                 'Good afternoon',
  'home.greet.evening':                   'Good evening',
  'home.headline.ok':                     'All clear',
  'home.headline.one':                    'One thing needs your attention',
  'home.headline.n':                      '{{count}} things need your attention',
  'home.sub.ok':                          'Nothing to do right now — enjoy the peace.',
  'home.sub.n':                           'Swipe right to handle, left to snooze.',
  'home.tourStart':                       'Start trip',
  'home.scan':                            'Scan',
  'home.task':                            'Task',
  'home.expense':                         'Expense',
  'home.overflow':                        'Also open',
  'home.stackEmpty':                      'Nothing urgent right now',
  'home.swipeLater':                      'Later',
  'home.swipeDone':                       'Done',

  // Feed action labels
  'feed.action.expiring':                 'Consumed',
  'feed.action.lowstock':                 'Shop',
  'feed.action.predictedLow':             'Shop',
  'feed.action.task':                     'Done',
  'feed.action.balance':                  'Settle',

  // ── Feed items (from feed.ts) ──────────────────────────────────────
  'feed.expired.n':                       'Expired {{days}} day{{plural}} ago',
  'feed.expiring.today':                  'Expires today',
  'feed.expiring.n':                      'Expires in {{days}} day{{plural}}',
  'feed.lowstock':                        'Only {{total}} of {{threshold}} left',
  'feed.predicted.tomorrow':              'Likely empty tomorrow',
  'feed.predicted.n':                      'Estimated {{days}} days remaining',
  'feed.task.overdue':                    'Overdue',
  'feed.task.today':                      'Due today',
  'feed.task.n':                          'Due in {{days}} day{{plural}}',
  'feed.balance.youOwe':                  'You owe {{name}}',
  'feed.balance.owesYou':                 '{{name}} owes you',

  // ── Household view — welcome / onboarding ──────────────────────────
  'hh.welcomeTagline':                    'Your household, finally in sync: pantry, shopping, tasks and finances in one place.',
  'hh.featureStock':                      'Track pantry',
  'hh.featureShopping':                   'Plan shopping',
  'hh.featureTasks':                      'Share tasks',
  'hh.featureFinances':                   'Split costs',
  'hh.createLabel':                       'Create household',
  'hh.createPlaceholder':                 'Name (e.g. Downtown flat)',
  'hh.createButton':                      'Start new household',
  'hh.joinLabel':                         'Join with code',
  'hh.joinPlaceholder':                   '8-character code',
  'hh.paste':                             'Paste',
  'hh.joinButton':                        'Join household',
  'hh.restoreTitle':                      'Restore account from another device',
  'hh.restoreUserId':                     'User ID',
  'hh.restorePlaceholder':                'Paste user ID',
  'hh.restoreButton':                     'Link device',
  'hh.restoreHint':                       'Find your user ID in the household menu under "Account".',

  // ── Household view — member view ───────────────────────────────────
  'hh.invite':                            'Invite',
  'hh.inviteCodeLabel':                   'Code for housemates:',
  'hh.copyCode':                          'Copy code',
  'hh.copyLink':                          'Copy link',
  'hh.codeCopied':                        'Code copied',
  'hh.linkCopied':                        'Link copied',
  'hh.locations':                         'Storage locations',
  'hh.newLocation':                       'New location',
  'hh.noLocations':                       'No locations defined (e.g. Kitchen, Fridge)',
  'hh.members':                           'Members',
  'hh.joinedSince':                       'Member since {{date}}',
  'hh.you':                               '(You)',
  'hh.removeMember':                      'Remove',
  'hh.profile':                           'Your profile',
  'hh.profileName':                       'Your name in the household',
  'hh.accountRecovery':                   'Account recovery:',
  'hh.saveUserId':                        'If you switch devices, save your user ID:',
  'hh.copyUserId':                        'Copy',
  'hh.classicView':                       'Classic view',
  'hh.classicDescription':                'The denser power-user views (categories, prices, nutrition, recurrences, split rules, payment history) are still available:',
  'hh.leaveHousehold':                    'Leave household',
  'hh.confirmLeave':                      'Really leave this household?',
  'hh.confirmRemove':                     'Really remove {{name}} from the household?',
  'hh.memberRemoved':                     '{{name}} removed',
  'hh.memberRemoveError':                 'Error removing member',

  // ── Household — locations ──────────────────────────────────────────
  'hh.addSubLocation':                    'Add sub-location',
  'hh.rename':                            'Rename',
  'hh.delete':                            'Delete',
  'hh.locationAddTitle':                  'Add location',
  'hh.subLocationAddTitle':               'Add sub-location',
  'hh.locationAdded':                     'Location added',
  'hh.subLocationAdded':                  'Sub-location added',
  'hh.locationAddError':                  'Error creating location',
  'hh.locationRenameTitle':               'Rename',
  'hh.locationRenamed':                   'Renamed',
  'hh.locationRenameError':               'Error renaming',
  'hh.locationDeleteWithChildren':        'Really delete "{{name}}" and all sub-locations? Items inside won\'t be deleted, but will lose their location.',
  'hh.locationDelete':                    'Really delete "{{name}}"? Items inside won\'t be deleted, but will lose their location.',
  'hh.locationDeleted':                   'Deleted',
  'hh.locationDeleteError':               'Error deleting',

  // ── Household — clipboard / account ────────────────────────────────
  'hh.clipboardEmpty':                    'Clipboard is empty',
  'hh.inserted':                          'Pasted',
  'hh.insertNotAllowed':                  'Paste not allowed — long-press the field and paste manually',
  'hh.codeRequired':                      'Code is required',
  'hh.enterUserId':                       'Please enter a user ID',
  'hh.userIdCopied':                      'User ID copied',
  'hh.copyFailed':                        'Copy failed — tap the user ID and copy manually',
  'hh.accountRestored':                   'Account restored',
  'hh.accountRestoredNoHousehold':        'Account restored (no household found)',
  'hh.accountRestoredReload':             'Account restored — reloading page',
  'hh.newCodeGenerated':                  'New code generated',
  'hh.error':                             'Error',

  // ── Rooms view ─────────────────────────────────────────────────────
  'rooms.title':                          'Rooms',
  'rooms.newRoom':                        'New room',
  'rooms.search':                         'Search items\u2026',
  'rooms.addRoom':                        'Add room',
  'rooms.createFirst':                    'Create first room',
  'rooms.noRooms':                        'No rooms yet.',
  'rooms.items':                          '{{count}} items',
  'rooms.areas':                          'Areas',
  'rooms.addArea':                        'Add area',
  'rooms.itemsHere':                      'Items here',
  'rooms.noItems':                        'No items in {{name}}',
  'rooms.noItemsHere':                    'No items here',
  'rooms.low':                            'low',
  'rooms.move':                           'Move',
  'rooms.noResults':                      'No results',
  'rooms.noLocation':                     'No location assigned',
  'rooms.nameRequired':                   'Name is required',
  'rooms.areaAdded':                      'Area added',
  'rooms.roomAdded':                      'Room added',
  'rooms.addError':                       'Error creating',

  // ── Rooms — move item modal ────────────────────────────────────────
  'rooms.moveItem':                       'Move {{name}}',
  'rooms.moveFrom':                       'From {{path}} ({{count}} available)',
  'rooms.moveQty':                        'Quantity',
  'rooms.moveTo':                         'New location',
  'rooms.moveConfirm':                    'Move',
  'rooms.moveQtyError':                   'Quantity must be greater than 0',
  'rooms.moveSameLocation':               'Destination is the same as current location',
  'rooms.movePartial':                    'Only {{moved}} of {{qty}} moved (not enough stock)',
  'rooms.moved':                          'Moved',
  'rooms.moveError':                      'Error moving item',

  // ── People view ────────────────────────────────────────────────────
  'people.title':                         'People',
  'people.newTask':                       'New task',
  'people.balanceTitle':                  'Household balance',
  'people.allBalanced':                   'All accounts balanced',
  'people.settleAll':                     'Settle all debts',
  'people.doneThisWeek':                  'Done this week',
  'people.nothingThisWeek':               'Nothing checked off this week yet',
  'people.members':                       'Members',
  'people.offen':                         '{{count}} open',
  'people.thisWeek':                      '{{count}} this week',
  'people.owes':                          'owes',
  'people.gets':                          'gets',

  // ── People — person detail modal ───────────────────────────────────
  'people.owesHousehold':                 'owes the household',
  'people.getsHousehold':                 'gets from the household',
  'people.balanced':                      'Balanced',
  'people.settleWith':                    'Settle with {{name}}',
  'people.openTasks':                     'Open tasks',
  'people.noOpenTasks':                   'No open tasks',
  'people.done':                          'Done',
  'people.doneRecently':                  '{{count}} recently completed',
  'people.fairness':                      'Fairness',
  'people.thisWeekLabel':                 'This week',
  'people.totalLabel':                    'All time',

  // ── Brief / Today view ─────────────────────────────────────────────
  'brief.today':                          'Today',
  'brief.addExpense':                     'Add expense',
  'brief.nNeedAttention':                 '{{count}} things need attention',
  'brief.allQuiet':                       'All quiet in the household',
  'brief.importantToday':                 "Here's what's important today.",
  'brief.noUrgent':                       'No urgent stock, tasks or finances pending.',
  'brief.quickTask':                      'Task',
  'brief.quickShopping':                  'Shopping',
  'brief.quickExpense':                   'Expense',
  'brief.expiringTitle':                  'Expiring soon',
  'brief.noExpiring':                     'Nothing expiring soon',
  'brief.suggestionsTitle':               'Shopping suggestions',

  // ── Capture sheet ──────────────────────────────────────────────────
  'capture.title':                        'What would you like to do?',
  'capture.shoppingTrip':                 'Start shopping trip',
  'capture.shoppingTripSub':              'Check off list & log prices',
  'capture.scan':                         'Scan',
  'capture.scanSub':                      'Scan barcode',
  'capture.manual':                       'Add manually',
  'capture.task':                         'Task',
  'capture.taskSub':                      'Create new task',
  'capture.toList':                       'To list',
  'capture.toListSub':                    'Add without trip',

  // ── Language setting ───────────────────────────────────────────────
  'settings.language':                    'Language',
  'settings.lang.de':                     'Deutsch',
  'settings.lang.en':                     'English',

  // ── Generic actions ────────────────────────────────────────────────
  'feed.action.budget':                    'View',
  'feed.budget.title':                     'Budget warning: {{category}}',
  'feed.budget.exceeded':                  'Budget exceeded ({{spent}} € of {{budget}} €)',
  'feed.budget.projected':                 'Projected about {{projected}} € (budget: {{budget}} €)',
  'hh.exportData':                         'Export data (JSON)',
  'hh.deleteAccount':                      'Delete account',
  'rooms.expiryCheck':                     'Check expiry dates',
  'rooms.expired':                         'Expired',
  'rooms.days':                            '{{count}} days',
  'rooms.noFixedPlace':                    'No fixed place',
  'rooms.takeOne':                         'Take one unit',
  'rooms.addStock':                        'Add stock',
  'rooms.inParent':                        'In {{name}}',
  'rooms.name':                            'Name',
  'rooms.placeRoom':                       'e.g. kitchen',
  'rooms.placeArea':                       'e.g. fridge',
  'rooms.noPlace':                         'No place',

  'home.tasksTitle':                       'Tasks',
  'home.viewAllTasks':                     'View all tasks',
  'capture.shoppingList':                  'View shopping list',
  'capture.allTasks':                      'All tasks',
  'capture.allTasksSub':                   'Complete task list',
  'capture.item':                          'Item',
  'capture.expense':                       'Expense',
  'tasks.title':                           'Tasks',
  'tasks.new':                             'New task',
  'tasks.open':                            'Open',
  'tasks.done':                            'Done',
  'tasks.daily':                           'Daily',
  'tasks.weekly':                          'Weekly',
  'tasks.monthly':                         'Monthly',
  'tasks.irregular':                       'As needed',
  'tasks.rotation':                        ' (rotation)',
  'tasks.free':                            'Free · whoever has time',
  'tasks.markDone':                        'Mark {{title}} done',
  'tasks.reopen':                          'Reopen {{title}}',
  'tasks.delete':                          'Delete {{title}}',
  'tasks.allDone':                         'Everything done!',
  'tasks.edit':                            'Edit task',
  'tasks.fieldTitle':                      'Title',
  'tasks.titlePlaceholder':                'What needs doing?',
  'tasks.description':                     'Description',
  'tasks.descPlaceholder':                 'Details...',
  'tasks.assignee':                        'Assigned to',
  'tasks.nobody':                          'Nobody',
  'tasks.due':                             'Due on',
  'tasks.recurrence':                      'Recurrence',
  'tasks.once':                            'One-off',
  'tasks.irregularLong':                   'As needed (irregular, open to everyone)',
  'tasks.irregularHint':                   'For tasks without a fixed rhythm (e.g. laundry) -- stays unassigned and open to everyone, is not reassigned after completion, and simply goes back to "open" when it is needed again.',
  'tasks.rotationTeam':                    'Team rotation (who takes turns?)',
  'tasks.checklist':                       'Checklist (subtasks)',
  'tasks.addStep':                         'Add step',
  'tasks.create':                          'Create task',
  'tasks.saveChanges':                     'Save changes',
  'tasks.titleRequired':                   'Title required',
  'tasks.created':                         'Task created',
  'tasks.createError':                     'Failed to create',
  'tasks.saved':                           'Saved',
  'tasks.saveError':                       'Failed to save',
  'tasks.updateError':                     'Failed to update',
  'tasks.irregularCompleted':              'Done! Task is open again whenever it is needed next.',
  'tasks.recurringNext':                   'Recurring task moved to the next round!',
  'tasks.recurringChecklistNext':          'Recurring task moved to the next round! (checklist reset)',
  'tasks.noChecklist':                     'No checklist items',
  'tasks.stepPlaceholder':                 'Step (e.g. mop floor)',
  'tasks.remove':                          'Remove',

  'expenses.cat.groceries':                'Groceries',
  'expenses.cat.rent':                     'Rent & housing',
  'expenses.cat.household':                'Household & drugstore',
  'expenses.cat.leisure':                  'Leisure',
  'expenses.cat.settlement':               'Debt settlement',
  'expenses.cat.sonstiges':                'Other',
  'expenses.title':                        'Finances',
  'expenses.paymentHistory':               'Payment history',
  'expenses.openPaymentHistory':           'Open payment history',
  'expenses.add':                          'Add expense',
  'expenses.settle':                       'Settle',
  'expenses.youOwe':                       'You owe {{name}}',
  'expenses.owesYou':                      '{{name}} owes you',
  'expenses.youPay':                       'Payable by you',
  'expenses.owedToYou':                    'Money is owed to you',
  'expenses.allBalanced':                  'You are settled up with everyone',
  'expenses.list':                         'Expenses',
  'expenses.paidBy':                       'Paid by {{name}}',
  'expenses.delete':                       'Delete',
  'expenses.deleteItem':                   'Delete {{title}}',
  'expenses.none':                         'No expenses yet',
  'expenses.dateUnknown':                  'Date unknown',
  'budgets.title':                         'Budgets this month',
  'budgets.short':                         'Budget',
  'budgets.spentOf':                       '{{spent}} € of {{budget}} €',
  'budgets.remaining':                     '{{amount}} € left',
  'budgets.over':                          '{{amount}} € over',
  'budgets.edit':                          'Edit budget',
  'budgets.none':                          'No monthly budgets yet',
  'budgets.add':                           'Add budget',
  'budgets.category':                      'Category',
  'budgets.monthly':                       'Monthly budget (€)',
  'budgets.remove':                        'Remove budget',
  'budgets.validRequired':                 'Please enter a valid budget',
  'budgets.saved':                         'Budget saved',
  'budgets.saveError':                     'Failed to save',
  'budgets.removed':                       'Budget removed',
  'budgets.removeError':                   'Failed to remove',
  'settle.history':                        'Settlement history',
  'settle.none':                           'No debt settlements recorded yet',
  'settle.settledExpenses':                'Settled expenses',
  'settle.noneSettled':                    'No expenses settled by settlements yet',
  'settle.title':                          'Settle debts',
  'settle.success':                        'Debts settled successfully!',
  'settle.error':                          'Failed to settle',
  'settle.instructions':                   'To bring all accounts to 0.00 €, these transfers are needed:',
  'settle.markPaid':                       'Mark as paid (record settlement)',
  'settle.expenseTitle':                   'Debt settlement: {{from}} → {{to}}',
  'expense.edit':                          'Edit expense',
  'expense.add':                           'Add expense',
  'expense.fieldTitle':                    'Title',
  'expense.titlePlaceholder':              'e.g. weekly groceries',
  'expense.category':                      'Category',
  'expense.saveChanges':                   'Save changes',
  'expense.create':                        'Create expense',
  'expense.created':                       'Expense created',
  'expense.updated':                       'Expense updated',
  'expense.saveError':                     'Failed to save',
  'expense.updateError':                   'Failed to update',

  'capture.tripSub':                       'Check off list & log prices',
  'capture.receipt':                       'Scan receipt',
  'capture.receiptSub':                    'Receipt photo',
  'capture.addToList':                     'To list',
  'capture.addToListSub':                  'Add without a trip',
  'capture.shoppingListSub':               'Edit list without a trip',
  'shopping.aisle.obst':                   'Fruit & vegetables',
  'shopping.aisle.gemuese':                'Fruit & vegetables',
  'shopping.aisle.milch':                  'Chilled & dairy',
  'shopping.aisle.proteine':               'Meat & protein',
  'shopping.aisle.getreide':               'Grains & sides',
  'shopping.aisle.getraenke':              'Drinks',
  'shopping.aisle.fette':                  'Oils & fats',
  'shopping.aisle.fertig':                 'Canned & ready-made',
  'shopping.aisle.sonstiges':              'Other & household',
  'shopping.title':                        'Shopping',
  'shopping.scanBarcode':                  'Scan barcode',
  'shopping.addManual':                    'Add manually',
  'shopping.openByAisle':                  'Open (by supermarket aisle)',
  'shopping.suggestion':                   'Suggestion',
  'shopping.lowStockSuggestion':           'Below minimum stock (+{{count}} recommended)',
  'shopping.boughtLog':                    'Bought (log quantity/expiry/price)',
  'shopping.dismissSuggestion':            'Hide suggestion',
  'shopping.delete':                       'Delete',
  'shopping.empty':                        'Nothing on the list',
  'shopping.done':                         'Done',
  'shopping.reopen':                       'Reopen {{name}}',
  'shopping.deleteItem':                   'Delete {{name}}',
  'shopping.addToListTitle':               'Add to list',
  'shopping.item':                         'Item',
  'shopping.needPlaceholder':              'What is needed?',
  'shopping.quantityOptional':             'Quantity (optional)',
  'shopping.qtyPlaceholder':               'e.g. 2 packs',
  'shopping.priceOptional':                'Price (€, optional)',
  'shopping.pricePlaceholder':             'e.g. 1.99',
  'shopping.add':                          'Add',
  'inventory.cat.sonstiges':               'Other',
  'inventory.cat.getraenke':               'Drinks',
  'inventory.cat.getreide':                'Grains',
  'inventory.cat.gemuese':                 'Vegetables',
  'inventory.cat.obst':                    'Fruit',
  'inventory.cat.milch':                   'Dairy',
  'inventory.cat.proteine':                'Protein',
  'inventory.cat.fette':                   'Fats',
  'inventory.cat.fertig':                  'Ready-made',

  'capture.itemSub':                       'Add manually',
  'capture.expenseSub':                    'Record costs',
  'expense.amount':                        'Amount (€)',
  'expense.paidBy':                        'Paid by',
  'expense.split':                         'Split',
  'expense.splitEqual':                    'Equal',
  'expense.splitPercent':                  'Percent (%)',
  'expense.splitAmounts':                  'Amounts (€)',
  'expense.saveDefaultSplit':              'Save as default for category',
  'expense.total':                         'Total: {{value}}',
  'expense.defaultLoaded':                 'Default split for category loaded',
  'expense.defaultSaved':                  'Default split (%) saved for category!',
  'expense.validRequired':                 'Please enter valid data',
  'expense.onePersonRequired':             'Select at least one person',

  'realtime.connected':                    'Live sync connected',
  'realtime.connecting':                   'Live sync connecting…',
  'realtime.fallback':                     'Live sync disconnected — polling fallback active',
  'realtime.disconnected':                 'Live sync disconnected',
  'presence.shoppingOne':                  '{{name}} is shopping now',
  'presence.shoppingMany':                 '{{names}} are shopping now',
  'presence.shoppingAlsoOne':              '{{name}} is also shopping now',
  'presence.shoppingAlsoMany':             '{{names}} are also shopping now',

  'trip.close':                            'Close shopping trip',
  'trip.title':                            'Shopping trip',
  'trip.finish':                           'Finish',
  'trip.barcode':                          'Barcode',
  'trip.noBarcode':                        'No barcode',
  'trip.empty':                            'Nothing in this trip yet. Scan an item or add something without a barcode.',
  'trip.finishWithOpen':                   'Finish shopping (leave {{count}} open)',
  'trip.ended':                            'Shopping finished',
  'trip.checkedItems':                     'From checked-off items',
  'trip.paidAtCheckout':                   'Paid at checkout (€)',
  'trip.discountHint':                     'Correct discounts or receipt differences here before it becomes an expense.',
  'trip.boughtOpen':                       '{{done}} bought · {{open}} stay open',
  'trip.logExpense':                       'Record as expense',
  'trip.back':                             'Back to trip',
  'trip.closeWithoutExpense':              'Close without expense',
  'trip.quantity':                         'Quantity',
  'trip.pricePaid':                        'Price paid (€)',
  'trip.checkContinue':                    'Check off & continue',
  'trip.manualAdd':                        'Add extra purchase',
  'trip.item':                             'Item',
  'trip.qtyOptional':                      'Quantity (optional)',
  'trip.addToTrip':                        'Add to trip',
  'trip.nameRequired':                     'Name required',
  'trip.added':                            'Added to trip',
  'trip.addError':                         'Failed to add',
  'trip.bookingError':                     'Failed to book',
  'trip.updateError':                      'Failed to update',
  'trip.searching':                        'Looking up product...',
  'trip.scanError':                        'Scan failed',
  'trip.noAmount':                         'No amount to book',
  'trip.expenseTitle':                     'Shopping trip',
  'trip.loggedExpense':                    'Recorded as expense',

  'action.save':                          'Save',
  'action.cancel':                        'Cancel',
};

export default en;
